import { useState } from 'react'
import ReactMarkdown from 'react-markdown'
import { ArrowUp, Bell, History, MessageSquare, MessageSquarePlus } from 'lucide-react'
import { Badge, Button, Card, EmptyState, Select, TextAreaField, useToast } from '@/components/play'
import { useFeedbackActions } from '@/hooks/useFeedback'
import {
  feedbackKindBadgeTone,
  feedbackKindLabels,
  feedbackPriorityBadgeTone,
  feedbackPriorityLabels,
  feedbackStatusBadgeTone,
  feedbackStatusLabels,
  formatRelativeTime,
} from '@/lib/feedback'
import type { FeedbackItemResource, FeedbackPriority, FeedbackStatus } from '@/types/feedback'
import { api } from '@/util/api'

interface FeedbackDetailProps {
  item: FeedbackItemResource
  onItemChange: (item: FeedbackItemResource) => void
}

const triageStatuses: FeedbackStatus[] = ['open', 'planned', 'in_progress', 'done', 'closed']
const triagePriorities: Exclude<FeedbackPriority, null>[] = ['low', 'medium', 'high', 'critical']
const statusItems = triageStatuses.map((status) => ({ value: status, label: feedbackStatusLabels[status] }))
const priorityItems = triagePriorities.map((priority) => ({ value: priority, label: feedbackPriorityLabels[priority] }))

export function FeedbackDetail({ item, onItemChange }: FeedbackDetailProps) {
  const { toast } = useToast()
  const { addFeedbackComment, changeFeedbackPriority, changeFeedbackStatus, followFeedbackItem, unfollowFeedbackItem, voteFeedbackItem, unvoteFeedbackItem } = useFeedbackActions()
  const [commentBody, setCommentBody] = useState('')
  const [busyAction, setBusyAction] = useState<string | null>(null)
  const canManage = item.attributes.can_manage
  const canViewContext = item.attributes.can_edit || canManage

  async function toggleVote() {
    const nextAction = item.attributes.viewer_has_voted ? unvoteFeedbackItem : voteFeedbackItem
    setBusyAction('vote')
    try {
      const response = await nextAction(item.id)
      onItemChange(response.data)
    } catch (err: any) {
      toast({
        title: 'Vote failed',
        description: err?.response?.data?.error?.message || 'Your vote wasn’t saved. Try again.',
        tone: 'error',
      })
    } finally {
      setBusyAction(null)
    }
  }

  async function toggleFollow() {
    const nextAction = item.attributes.viewer_is_following ? unfollowFeedbackItem : followFeedbackItem
    setBusyAction('follow')
    try {
      const response = await nextAction(item.id)
      onItemChange(response.data)
    } catch (err: any) {
      toast({
        title: 'Follow failed',
        description: err?.response?.data?.error?.message || 'We couldn’t update following. Try again.',
        tone: 'error',
      })
    } finally {
      setBusyAction(null)
    }
  }

  async function submitComment() {
    if (!commentBody.trim()) return

    setBusyAction('comment')
    try {
      await addFeedbackComment(item.id, commentBody.trim())
      const refreshed = await api.getFeedbackItem(item.id)
      onItemChange(refreshed.data)
      setCommentBody('')
    } catch (err: any) {
      toast({
        title: 'Comment failed',
        description: err?.response?.data?.error?.message || 'Your comment wasn’t posted. Try again.',
        tone: 'error',
      })
    } finally {
      setBusyAction(null)
    }
  }

  async function updateStatus(status: FeedbackStatus) {
    setBusyAction('status')
    try {
      const response = await changeFeedbackStatus(item.id, status)
      onItemChange(response.data)
    } catch (err: any) {
      toast({
        title: 'Status update failed',
        description: err?.response?.data?.error?.message || 'The status wasn’t changed. Try again.',
        tone: 'error',
      })
    } finally {
      setBusyAction(null)
    }
  }

  async function updatePriority(priority: Exclude<FeedbackPriority, null>) {
    setBusyAction('priority')
    try {
      const response = await changeFeedbackPriority(item.id, priority)
      onItemChange(response.data)
    } catch (err: any) {
      toast({
        title: 'Priority update failed',
        description: err?.response?.data?.error?.message || 'The priority wasn’t changed. Try again.',
        tone: 'error',
      })
    } finally {
      setBusyAction(null)
    }
  }

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
      <div className="space-y-6">
        <Card as="section">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={feedbackKindBadgeTone(item.attributes.kind)}>{feedbackKindLabels[item.attributes.kind]}</Badge>
            <Badge tone={feedbackStatusBadgeTone(item.attributes.status)}>{feedbackStatusLabels[item.attributes.status]}</Badge>
            {item.attributes.priority ? (
              <Badge tone={feedbackPriorityBadgeTone(item.attributes.priority)}>{feedbackPriorityLabels[item.attributes.priority]}</Badge>
            ) : null}
          </div>

          <h1 className="mt-4 text-3xl font-semibold text-neutral-10">{item.attributes.title}</h1>
          <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-neutral-7">
            <span>Opened by {item.attributes.reporter.name}</span>
            <span>Updated {formatRelativeTime(item.attributes.updated_at)}</span>
            {item.attributes.duplicate_of_title ? <span>Duplicate of {item.attributes.duplicate_of_title}</span> : null}
          </div>

          <div className="prose mt-6 max-w-none prose-headings:text-neutral-10 prose-p:text-neutral-8 prose-a:text-neutral-10 prose-li:text-neutral-8 prose-strong:text-neutral-10">
            <ReactMarkdown>{item.attributes.description_markdown}</ReactMarkdown>
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            {item.attributes.kind === 'bug' || item.attributes.kind === 'feature_request' ? (
              // Toggles: the pressed fill says voted or following; the yellow primary stays with Post comment.
              <Button
                onClick={toggleVote}
                loading={busyAction === 'vote'}
                variant="secondary"
                aria-pressed={item.attributes.viewer_has_voted}
              >
                <ArrowUp aria-hidden="true" />
                Vote · {item.attributes.votes_count}
              </Button>
            ) : null}
            <Button
              onClick={toggleFollow}
              loading={busyAction === 'follow'}
              variant="secondary"
              aria-pressed={item.attributes.viewer_is_following}
            >
              <Bell aria-hidden="true" />
              Follow · {item.attributes.followers_count}
            </Button>
          </div>
        </Card>

        <Card as="section" title="Comments" icon={<MessageSquarePlus />}>
          <div className="space-y-4">
            {(item.attributes.comments || []).map((comment) => (
              <article key={comment.id} className="rounded-lg bg-neutral-1 p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium text-neutral-10">{comment.attributes.author.name}</p>
                    <p className="text-xs text-neutral-7">{formatRelativeTime(comment.attributes.created_at)}</p>
                  </div>
                  {comment.attributes.system ? <Badge>System</Badge> : null}
                </div>
                <div className="prose mt-3 max-w-none prose-headings:text-neutral-10 prose-p:text-neutral-8 prose-a:text-neutral-10 prose-li:text-neutral-8 prose-strong:text-neutral-10">
                  <ReactMarkdown>{comment.attributes.body_markdown}</ReactMarkdown>
                </div>
              </article>
            ))}
            {(item.attributes.comments || []).length === 0 ? (
              <EmptyState size="compact" headingLevel={3} icon={<MessageSquare />} title="No comments yet" />
            ) : null}
          </div>

          <div className="mt-6 space-y-3">
            <TextAreaField
              id="feedback-comment"
              label="Add a comment"
              value={commentBody}
              onChange={(event) => setCommentBody(event.target.value)}
              placeholder="Share more detail, a workaround, or next steps."
            />
            <Button onClick={submitComment} loading={busyAction === 'comment'} disabled={!commentBody.trim()}>
              Post comment
            </Button>
          </div>
        </Card>
      </div>

      <aside className="space-y-6">
        <Card as="section" title="Context">
          <dl className="space-y-3 text-sm">
            <div>
              <dt className="text-neutral-7">Section</dt>
              <dd className="text-neutral-10">
                {(() => {
                  const fullPath = String(item.attributes.context.page_path || item.attributes.context.page_url || '')
                  if (canViewContext) return fullPath || 'Unknown'
                  const match = fullPath.match(/^\/([^/]+)/)
                  return match ? `/${match[1]}` : 'Unknown'
                })()}
              </dd>
            </div>
            {canViewContext && (
              <>
                <div>
                  <dt className="text-neutral-7">Browser</dt>
                  <dd className="text-neutral-10">{String(item.attributes.context.user_agent || 'Unknown')}</dd>
                </div>
                <div>
                  <dt className="text-neutral-7">Timezone</dt>
                  <dd className="text-neutral-10">{String(item.attributes.context.timezone || 'Unknown')}</dd>
                </div>
              </>
            )}
          </dl>
        </Card>

        <Card as="section" title="Triage">
          <div className="space-y-4">
            {canManage ? (
              <Select
                label="Status"
                items={statusItems}
                value={item.attributes.status}
                onValueChange={(value) => {
                  if (value) void updateStatus(value)
                }}
              />
            ) : (
              <div className="space-y-2">
                <p className="text-sm font-medium text-neutral-10">Status</p>
                <Badge tone={feedbackStatusBadgeTone(item.attributes.status)}>{feedbackStatusLabels[item.attributes.status]}</Badge>
              </div>
            )}
            {canManage ? (
              <Select
                label="Priority"
                items={priorityItems}
                // An unprioritized item reads as such, and any priority, Medium included, can then be set.
                value={item.attributes.priority}
                placeholder="Not prioritized"
                onValueChange={(value) => {
                  if (value) void updatePriority(value)
                }}
              />
            ) : (
              <div className="space-y-2">
                <p className="text-sm font-medium text-neutral-10">Priority</p>
                {item.attributes.priority ? (
                  <Badge tone={feedbackPriorityBadgeTone(item.attributes.priority)}>{feedbackPriorityLabels[item.attributes.priority]}</Badge>
                ) : (
                  <p className="text-sm text-neutral-7">Not prioritized</p>
                )}
              </div>
            )}
          </div>
        </Card>

        <Card as="section" title="History">
          <div className="space-y-4">
            {(item.attributes.status_transitions || []).map((transition) => (
              <div key={transition.id} className="rounded-lg bg-neutral-1 p-3">
                <p className="text-sm font-medium text-neutral-10">
                  {transition.attributes.changed_by.name} moved this from {feedbackStatusLabels[transition.attributes.from_status]} to {feedbackStatusLabels[transition.attributes.to_status]}
                </p>
                {transition.attributes.reason ? (
                  <p className="mt-2 text-sm text-neutral-7">{transition.attributes.reason}</p>
                ) : null}
                <p className="mt-2 text-xs text-neutral-7">{formatRelativeTime(transition.attributes.created_at)}</p>
              </div>
            ))}
            {(item.attributes.status_transitions || []).length === 0 ? (
              <EmptyState size="compact" headingLevel={3} icon={<History />} title="No status changes yet" />
            ) : null}
          </div>
        </Card>
      </aside>
    </div>
  )
}
